import { useTranslation } from "react-i18next";
import { components } from "../../types/api";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { useDispatch, useSelector } from "react-redux";
import { setToastMessage } from "../../store/slices/app/appSlice";
import { selectUserDb } from "../../store/slices/session/sessionSelector";
import {
  formatCurrency,
  formatNumber,
  formatPercentageNoMultiply,
} from "../../utils/formatUtils";
import MetricsTimelineChart, {
  type MetricDataPoint,
  type MetricType,
} from "../card/MetricsTimelineChart";
import { exportMetricsToCSV } from "../../utils/exportUtils";
import { useGetMergedInsertionOrderMetricsQuery } from "../../store/apis/internalCampaignsMergeApi";
import InsertionOrdersTable from "../table/InsertionOrdersTable";
import InsertionOrderMetricsChart from "../chart/InsertionOrderMetricsChart";
import CampaignDetailsCard from "../card/CampaignDetailsCard";

interface AmazonDspMergeCampaignDetailsProps {
  isLoadingMergedCampaign: boolean;
  mergedCampaignId: string | undefined;
  mergedCampaignData:
    | components["schemas"]["MergedCampaignMetrics"]
    | null
    | undefined;
  metricsStartDate?: string;
  metricsEndDate?: string;
  onMetricsStartDateChange?: (value: string) => void;
  onMetricsEndDateChange?: (value: string) => void;
  onClearMetricsDateFilters?: () => void;
}

const normalizeRate = (value?: number) => {
  if (value === undefined || value === null) {
    return 0;
  }
  return value > 1 ? value / 100 : value;
};

type CompleteViewsSource =
  | { totalCompleteViews?: number; visualizations?: number }
  | components["schemas"]["DailyMetrics"];

const resolveCompleteViews = (summary: CompleteViewsSource) => {
  if ("totalCompleteViews" in summary && summary.totalCompleteViews != null) {
    return summary.totalCompleteViews;
  }
  if ("visualizations" in summary && summary.visualizations != null) {
    return summary.visualizations;
  }
  if ("completeViews" in summary && summary.completeViews != null) {
    return summary.completeViews;
  }
  return 0;
};

const AmazonDspMergeCampaignDetails: React.FC<
  AmazonDspMergeCampaignDetailsProps
> = ({
  isLoadingMergedCampaign,
  mergedCampaignId,
  mergedCampaignData,
  metricsStartDate,
  metricsEndDate,
  onMetricsStartDateChange,
  onMetricsEndDateChange,
  onClearMetricsDateFilters,
}) => {
  const getChartDataPoints = (
    dailyData: components["schemas"]["DailyMetrics"][] = []
  ): MetricDataPoint[] =>
    dailyData.map((day) => ({
      date: day.date,
      ctr: day.ctr ?? 0,
      cpc: day.cpc ?? 0,
      cpm: day.cpm ?? 0,
      impressions: day.impressions ?? 0,
      clicks: day.clicks ?? 0,
      cost: day.cost ?? 0,
      completeViews: day.completeViews ?? 0,
      vtr: normalizeRate(day.vtr ?? 0),
    }));

  const format = "VIDEO" as const;
  const [internalStartDate, setInternalStartDate] = useState<string>("");
  const [internalEndDate, setInternalEndDate] = useState<string>("");
  const [lineItemStartDate, setLineItemStartDate] = useState<string>("");
  const [lineItemEndDate, setLineItemEndDate] = useState<string>("");
  const [insertionOrderStartDate, setInsertionOrderStartDate] =
    useState<string>("");
  const [insertionOrderEndDate, setInsertionOrderEndDate] =
    useState<string>("");
  const [selectedInsertionOrder, setSelectedInsertionOrder] = useState<{
    id?: string;
    name?: string;
    dailyData: components["schemas"]["DailyLineItemMetrics"][];
    summary: components["schemas"]["MetricsLineItemSummary"];
  } | null>(null);

  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();

  const {
    data: insertionOrderMetrics,
    isLoading: isLoadingInsertionOrderMetrics,
    error: insertionOrderMetricsError,
  } = useGetMergedInsertionOrderMetricsQuery(
    {
      mergedCampaignId: mergedCampaignId || "",
      startDate: insertionOrderStartDate,
      endDate: insertionOrderEndDate,
    },
    {
      skip: !mergedCampaignId,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    }
  );

  const { metrics: resolvedInsertionOrderMetrics, usedCampaignMetricFallback } =
    useMemo(() => {
      if (!insertionOrderMetrics?.insertionOrders) {
        return {
          metrics: insertionOrderMetrics,
          usedCampaignMetricFallback: false,
        };
      }

      const reportedCompleteViewsTotal =
        insertionOrderMetrics.insertionOrders.reduce(
          (sum, order) => sum + resolveCompleteViews(order.summary),
          0
        );

      const fallbackCompleteViews = resolveCompleteViews(
        mergedCampaignData?.metrics?.summary ?? {}
      );

      const remainingFallbackPool = Math.max(
        0,
        fallbackCompleteViews - reportedCompleteViewsTotal
      );

      if (remainingFallbackPool <= 0) {
        return {
          metrics: insertionOrderMetrics,
          usedCampaignMetricFallback: false,
        };
      }

      const ordersNeedingFallback =
        insertionOrderMetrics.insertionOrders.filter(
          (order) => resolveCompleteViews(order.summary) <= 0
        );

      const impressionsForFallback = ordersNeedingFallback.reduce(
        (sum, order) => sum + (order.summary.totalImpressions ?? 0),
        0
      );

      if (ordersNeedingFallback.length === 0 || impressionsForFallback <= 0) {
        return {
          metrics: insertionOrderMetrics,
          usedCampaignMetricFallback: false,
        };
      }

      const campaignAvgVtr = mergedCampaignData?.metrics?.summary?.avgVTR ?? 0;

      const adjustedOrders = insertionOrderMetrics.insertionOrders.map(
        (order) => {
          const currentCompleteViews = resolveCompleteViews(order.summary);
          if (currentCompleteViews > 0) {
            return order;
          }

          const impressions = order.summary.totalImpressions ?? 0;
          const allocatedCompleteViews =
            impressions > 0
              ? (remainingFallbackPool * impressions) / impressionsForFallback
              : 0;

          const fallbackAvgVtr =
            impressions > 0
              ? (allocatedCompleteViews / impressions) * 100
              : campaignAvgVtr;

          return {
            ...order,
            summary: {
              ...order.summary,
              totalCompleteViews: Math.round(allocatedCompleteViews),
              avgVTR:
                order.summary.avgVTR && order.summary.avgVTR > 0
                  ? order.summary.avgVTR
                  : fallbackAvgVtr,
            },
          };
        }
      );

      return {
        metrics: {
          ...insertionOrderMetrics,
          insertionOrders: adjustedOrders,
        },
        usedCampaignMetricFallback: true,
      };
    }, [insertionOrderMetrics, mergedCampaignData]);

  const formatDateForInput = useCallback(
    (dateString: string | undefined): string => {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toISOString().split("T")[0];
    },
    []
  );

  const resolvedStartDate = metricsStartDate ?? internalStartDate;
  const resolvedEndDate = metricsEndDate ?? internalEndDate;

  const handleMetricsStartDateChange = useCallback(
    (value: string) => {
      if (onMetricsStartDateChange) {
        onMetricsStartDateChange(value);
      } else {
        setInternalStartDate(value);
      }

      if (!value) {
        if (onMetricsEndDateChange) {
          onMetricsEndDateChange("");
        } else {
          setInternalEndDate("");
        }
      }
    },
    [onMetricsStartDateChange, onMetricsEndDateChange]
  );

  const handleMetricsEndDateChange = useCallback(
    (value: string) => {
      if (onMetricsEndDateChange) {
        onMetricsEndDateChange(value);
      } else {
        setInternalEndDate(value);
      }
    },
    [onMetricsEndDateChange]
  );

  const handleClearMetricsDates = useCallback(() => {
    if (onClearMetricsDateFilters) {
      onClearMetricsDateFilters();
    } else {
      setInternalStartDate("");
      setInternalEndDate("");
    }
  }, [onClearMetricsDateFilters]);

  // Initialize date fields with campaign dates when data loads
  useEffect(() => {
    if (mergedCampaignData?.startDate && !resolvedStartDate) {
      handleMetricsStartDateChange(
        formatDateForInput(mergedCampaignData.startDate)
      );
    }
    if (mergedCampaignData?.endDate && !resolvedEndDate) {
      handleMetricsEndDateChange(
        formatDateForInput(mergedCampaignData.endDate)
      );
    }
  }, [
    mergedCampaignData,
    formatDateForInput,
    resolvedStartDate,
    resolvedEndDate,
    handleMetricsStartDateChange,
    handleMetricsEndDateChange,
  ]);

  // Initialize date fields with campaign dates when data loads
  useEffect(() => {
    if (mergedCampaignData?.startDate && !lineItemStartDate) {
      setLineItemStartDate(formatDateForInput(mergedCampaignData.startDate));
    }
    if (mergedCampaignData?.endDate && !lineItemEndDate) {
      setLineItemEndDate(formatDateForInput(mergedCampaignData.endDate));
    }
    if (mergedCampaignData?.startDate && !insertionOrderStartDate) {
      setInsertionOrderStartDate(
        formatDateForInput(mergedCampaignData.startDate)
      );
    }
    if (mergedCampaignData?.endDate && !insertionOrderEndDate) {
      setInsertionOrderEndDate(formatDateForInput(mergedCampaignData.endDate));
    }
  }, [
    mergedCampaignData,
    formatDateForInput,
    lineItemStartDate,
    lineItemEndDate,
    insertionOrderStartDate,
    insertionOrderEndDate,
  ]);

  // Set the first insertion order as selected when data loads
  useEffect(() => {
    if (
      resolvedInsertionOrderMetrics?.insertionOrders &&
      resolvedInsertionOrderMetrics.insertionOrders.length > 0 &&
      !selectedInsertionOrder
    ) {
      setSelectedInsertionOrder(
        resolvedInsertionOrderMetrics.insertionOrders[0]
      );
    }
  }, [resolvedInsertionOrderMetrics, selectedInsertionOrder]);

  useEffect(() => {
    if (insertionOrderMetricsError) {
      dispatch(
        setToastMessage({
          message: t("common.errorFetchingData", {
            defaultValue: "Error fetching data",
          }),
          severity: "error",
        })
      );
    }
  }, [insertionOrderMetricsError, dispatch, t]);

  const handleInsertionOrderClick = useCallback(
    (insertionOrder: {
      id?: string;
      name?: string;
      dailyData: components["schemas"]["DailyLineItemMetrics"][];
      summary: components["schemas"]["MetricsLineItemSummary"];
    }) => {
      setSelectedInsertionOrder(insertionOrder);
    },
    []
  );

  const userDb = useSelector(selectUserDb);
  const isAdmin = userDb?.role === "ADMIN";

  const handleExportInsertionOrderMetrics = useCallback(() => {
    if (!insertionOrderMetrics?.insertionOrders) {
      return;
    }

    const csvData = [];

    // Add header row based on Amazon DSP requirements
    const headers = [t("campaigns.name", { defaultValue: "Name" })];
    headers.push(
      t("campaigns.costValue", { defaultValue: "Cost" }),
      t("campaigns.impressions", { defaultValue: "Impressions" }),
      t("campaigns.completeViews", { defaultValue: "Complete Views" }),
      t("campaigns.cpm", { defaultValue: "CPM" }),
      t("campaigns.vtr", { defaultValue: "VTR" })
    );

    // Add Real Cost header only for admin users
    if (isAdmin) {
      headers.push(t("campaigns.realCost", { defaultValue: "Real Cost" }));
    }

    csvData.push(headers);

    // Add data rows
    resolvedInsertionOrderMetrics?.insertionOrders.forEach((insertionOrder) => {
      const row = [insertionOrder.name || "Unnamed AdOrder"];
      row.push(
        formatCurrency(
          insertionOrder.summary.totalCost,
          mergedCampaignData?.currency_enum
        ),
        formatNumber(insertionOrder.summary.totalImpressions ?? 0),
        formatNumber(resolveCompleteViews(insertionOrder.summary)),
        formatCurrency(
          insertionOrder.summary.avgCPM,
          mergedCampaignData?.currency_enum
        ),
        formatPercentageNoMultiply(insertionOrder.summary.avgVTR || 0)
      );

      // Add Real Cost data only for admin users
      if (isAdmin && insertionOrder.summary.totalRealCost !== undefined) {
        row.push(
          formatCurrency(
            insertionOrder.summary.totalRealCost,
            mergedCampaignData?.currency_enum
          )
        );
      }

      csvData.push(row);
    });

    // Generate CSV and download
    const BOM = new Uint8Array([0xef, 0xbb, 0xbf]);
    const csvContent = csvData.map((row) => row.join(",")).join("\n");
    const blob = new Blob([BOM, csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `ad_orders_${mergedCampaignId}_${new Date()
        .toISOString()
        .slice(0, 10)}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [
    insertionOrderMetrics?.insertionOrders,
    t,
    isAdmin,
    resolvedInsertionOrderMetrics?.insertionOrders,
    mergedCampaignId,
    mergedCampaignData?.currency_enum,
  ]);

  const handleExportMetrics = useCallback(() => {
    if (!mergedCampaignData?.metrics?.dailyData) {
      dispatch(
        setToastMessage({
          message: t("common.noDataToExport", {
            defaultValue: "No data to export",
          }),
          severity: "warning",
        })
      );
      return;
    }

    // Transform the data based on format - only include metrics that are displayed in cards
    const formattedData = mergedCampaignData?.metrics?.dailyData.map((day) => {
      const impressions = day.impressions || 0;
      const cost = day.cost || 0;
      const ctrValue = day.ctr || 0;

      return {
        date: day.date,
        cost,
        impressions,
        clicks: day.clicks || 0,
        ctr: ctrValue,
        cpm: day.cpm || 0,
        cpc: day.cpc || 0,
        cpv: day.cpv || 0,
        vtr: day.vtr || 0,
        dailyInvestment: cost,
        dailyImpressions: impressions,
        completeViews: resolveCompleteViews(day),
        dailyCTR: ctrValue,
        conversions: 0,
      };
    });

    exportMetricsToCSV(
      formattedData,
      mergedCampaignData?.name || "export",
      t,
      dispatch
    );
  }, [mergedCampaignData, t, dispatch]);

  return (
    <>
      {isLoadingMergedCampaign ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "50vh",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Metrics */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              borderRadius: 2,
              border: `1px solid ${theme.palette.grey[200]}`,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: { xs: "flex-start", sm: "center" },
                flexDirection: { xs: "column", sm: "row" },
                gap: { xs: 2, sm: 0 },
                mb: 2,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t("campaigns.metrics", { defaultValue: "Metrics" })}
              </Typography>

              {/* Date Filter */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  rowGap: 2,
                  flexWrap: "wrap",
                }}
              >
                <TextField
                  label={t("common.startDate", {
                    defaultValue: "Start Date",
                  })}
                  type="date"
                  size="small"
                  value={resolvedStartDate || ""}
                  onChange={(e) => handleMetricsStartDateChange(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    min: formatDateForInput(mergedCampaignData?.startDate),
                    max:
                      resolvedEndDate ||
                      formatDateForInput(mergedCampaignData?.endDate),
                  }}
                  sx={{ width: 150 }}
                />

                <TextField
                  label={t("common.endDate", {
                    defaultValue: "End Date",
                  })}
                  type="date"
                  size="small"
                  value={resolvedEndDate || ""}
                  onChange={(e) => handleMetricsEndDateChange(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    min:
                      resolvedStartDate ||
                      formatDateForInput(mergedCampaignData?.startDate),
                    max: formatDateForInput(mergedCampaignData?.endDate),
                  }}
                  sx={{ width: 150 }}
                  disabled={!resolvedStartDate}
                />

                <IconButton
                  onClick={handleClearMetricsDates}
                  size="small"
                  color="primary"
                  title={t("common.clearFilters", {
                    defaultValue: "Clear Filters",
                  })}
                >
                  <ClearIcon />
                </IconButton>

                {/* Export Button */}
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  startIcon={<FileDownloadIcon />}
                  onClick={handleExportMetrics}
                  sx={{
                    mt: { xs: 1, sm: 0 },
                    ml: { xs: 0, sm: 1 },
                    height: 36,
                    textTransform: "none",
                    fontWeight: 500,
                  }}
                >
                  {t("campaigns.exportMetrics", {
                    defaultValue: "Export Data",
                  })}
                </Button>
              </Box>
            </Box>
            <Divider sx={{ mb: 2 }} />

            {mergedCampaignData?.metrics?.summary && (
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 3,
                  "& > *": {
                    flex: "1 1 calc(20% - 24px)",
                    minWidth: "180px",
                  },
                }}
              >
                {[
                  {
                    label: t("campaigns.costValue", { defaultValue: "Cost" }),
                    value: formatCurrency(
                      mergedCampaignData.metrics.summary.totalCost,
                      mergedCampaignData.currency_enum
                    ),
                  },
                  {
                    label: t("campaigns.impressions", {
                      defaultValue: "Impressions",
                    }),
                    value: formatNumber(
                      mergedCampaignData.metrics.summary.totalImpressions
                    ),
                  },
                  {
                    label: t("campaigns.completeViews", {
                      defaultValue: "Complete Views",
                    }),
                    value: formatNumber(
                      resolveCompleteViews(mergedCampaignData.metrics.summary)
                    ),
                  },
                  {
                    label: t("campaigns.cpm", { defaultValue: "CPM" }),
                    value: formatCurrency(
                      mergedCampaignData.metrics.summary.avgCPM,
                      mergedCampaignData.currency_enum
                    ),
                  },
                  {
                    label: t("campaigns.vtr", { defaultValue: "VTR" }),
                    value: formatPercentageNoMultiply(
                      mergedCampaignData.metrics.summary.avgVTR || 0
                    ),
                  },
                ].map((card) => (
                  <CampaignDetailsCard
                    key={card.label}
                    label={card.label}
                    value={card.value}
                  />
                ))}
              </Box>
            )}

            {/* Metrics Timeline Chart */}
            {mergedCampaignData?.metrics?.dailyData &&
            mergedCampaignData.metrics.dailyData.length > 0 ? (
              <Box sx={{ mt: 4 }}>
                <MetricsTimelineChart
                  title={t("campaigns.metricsOverTime", {
                    defaultValue: "Metrics Over Time",
                  })}
                  subtitle={t("campaigns.clickMetricsToCompare", {
                    defaultValue:
                      "Click on metrics to compare or select 'All' to see accumulated data",
                  })}
                  data={getChartDataPoints(
                    mergedCampaignData.metrics.dailyData
                  )}
                  isLoading={isLoadingMergedCampaign}
                  hiddenMetrics={["ctr", "cpc", "clicks"] as MetricType[]}
                  metricLabelsOverride={{
                    cost: t("campaigns.costValue", { defaultValue: "Cost" }),
                    impressions: t("campaigns.impressions", {
                      defaultValue: "Impressions",
                    }),
                    completeViews: t("campaigns.completeViews", {
                      defaultValue: "Complete Views",
                    }),
                    cpm: t("campaigns.cpm", { defaultValue: "CPM" }),
                    vtr: t("campaigns.vtr", { defaultValue: "VTR" }),
                  }}
                />
              </Box>
            ) : (
              <Box
                sx={{
                  mt: 4,
                  p: 3,
                  textAlign: "center",
                  bgcolor: theme.palette.background.default,
                  borderRadius: 2,
                }}
              >
                <Typography variant="body1" color="text.secondary">
                  {t("campaigns.noMetricsData", {
                    defaultValue:
                      "No metrics data available for the selected time period. Data will appear here once the campaign starts generating metrics.",
                  })}
                </Typography>
              </Box>
            )}

            {/* AdOrder Section */}
            <Paper
              sx={{
                p: 3,
                borderRadius: 2,
                backgroundColor: theme.palette.background.paper,
                mt: 3,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6" fontWeight={600} color="text.primary">
                  {t("campaigns.insertionOrders", {
                    defaultValue: "AdOrder",
                  })}
                </Typography>

                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  <TextField
                    label={t("campaigns.startDate", {
                      defaultValue: "Start Date",
                    })}
                    type="date"
                    size="small"
                    value={insertionOrderStartDate}
                    onChange={(e) => setInsertionOrderStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: 150 }}
                  />

                  <TextField
                    label={t("campaigns.endDate", {
                      defaultValue: "End Date",
                    })}
                    type="date"
                    size="small"
                    value={insertionOrderEndDate}
                    onChange={(e) => setInsertionOrderEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: 150 }}
                    disabled={!insertionOrderStartDate}
                  />

                  <IconButton
                    onClick={() => {
                      setInsertionOrderStartDate("");
                      setInsertionOrderEndDate("");
                    }}
                    size="small"
                    color="primary"
                    title={t("common.clearFilters", {
                      defaultValue: "Clear Filters",
                    })}
                  >
                    <ClearIcon />
                  </IconButton>

                  {/* Export Button */}
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    startIcon={<FileDownloadIcon />}
                    onClick={handleExportInsertionOrderMetrics}
                    sx={{
                      mt: { xs: 1, sm: 0 },
                      ml: { xs: 0, sm: 1 },
                      height: 36,
                      textTransform: "none",
                      fontWeight: 500,
                      minWidth: "auto",
                      whiteSpace: "nowrap",
                      px: 1.5,
                    }}
                  >
                    {t("campaigns.exportInsertionOrders", {
                      defaultValue: "Export",
                    })}
                  </Button>
                </Box>
              </Box>

              <Divider sx={{ mb: 2 }} />

              {/* AdOrder Table */}
              {isLoadingInsertionOrderMetrics ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : resolvedInsertionOrderMetrics?.insertionOrders &&
                resolvedInsertionOrderMetrics.insertionOrders.length > 0 ? (
                <>
                  {usedCampaignMetricFallback && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      {t("campaigns.amazonAdOrdersFallbackNotice", {
                        defaultValue:
                          "Amazon is returning zero AdOrder completes. Showing campaign-level complete views and VTR proportionally distributed by impressions.",
                      })}
                    </Typography>
                  )}
                  <InsertionOrdersTable
                    isLoading={isLoadingInsertionOrderMetrics}
                    insertionOrders={
                      resolvedInsertionOrderMetrics.insertionOrders
                    }
                    currency={mergedCampaignData?.currency_enum || "USD"}
                    onInsertionOrderClick={handleInsertionOrderClick}
                    selectedInsertionOrderId={selectedInsertionOrder?.id}
                    format={format}
                    variant="amazonCtv"
                  />

                  {/* Selected AdOrder Chart */}
                  {selectedInsertionOrder && (
                    <Box sx={{ mt: 3 }}>
                      <InsertionOrderMetricsChart
                        isLoading={isLoadingInsertionOrderMetrics}
                        dailyData={selectedInsertionOrder.dailyData}
                        title={selectedInsertionOrder.name || "Unnamed AdOrder"}
                        currency={mergedCampaignData?.currency_enum || "USD"}
                        isAdmin={isAdmin}
                      />
                    </Box>
                  )}
                </>
              ) : (
                <Typography variant="body1" sx={{ py: 2 }}>
                  {t("common.noData", {
                    defaultValue: "No insertion orders data available",
                  })}
                </Typography>
              )}
            </Paper>
          </Paper>
        </>
      )}
    </>
  );
};

export default AmazonDspMergeCampaignDetails;
